<template>
  <teleport to="body">
    <div
      v-if="showMenu"
      ref="widgetRightClickMenuItemRef"
      :style="positionStyle"
      class="widget-right-click-menu-wrapper"
      @click.stop=""
    >
      <el-scrollbar max-height="470px">
        <div v-for="(item, index) in menus" :key="index" class="item-wrapper">
          <template v-if="item.label === '选中'">
            <div
              class="text-wrapper"
              :class="{ 'active-item': isSelect }"
              @click.stop="triggerItem(item)"
            >
              <svg-icon v-if="isSelect" icon-class="confirm" class="confirm-icon"></svg-icon>
              <span>{{ `${item.label}"${widget.title}"` }}</span>
            </div>
          </template>
          <template v-else>
            <div class="text-wrapper" @click.stop="triggerItem(item)">
              <span>{{ item.label }}</span>
            </div>
          </template>
          <div v-if="item.splitLine" class="right-click-menu-item-split-line"></div>
        </div>
        <template v-if="widget.type !== 'Blank' && showLayer">
          <div class="right-click-menu-item-split-line"></div>
          <LayerItem
            :widget-page-info="layerItem"
            :comp="widget"
            @trigger-item="changeSelectWidget"
          />
        </template>
      </el-scrollbar>
    </div>
  </teleport>
</template>
<script setup lang="ts">
  import { useWidget } from '@haohan/clw-store'
  import type { Position } from '@haohan/clw-platform-public'
  import { computed, ref, watch } from 'vue'

  const widgetStore = useWidget()
  /**
   * 组件传入参数定义
   * position 鼠标事件触发得位置
   * showMenu 是否显示右键菜单
   */
  interface Props {
    position: Position
    showMenu: boolean
    menus: RightClickMenuItem[]
    widget: Widget
    showLayer?: boolean
  }
  const props = withDefaults(defineProps<Props>(), {
    showMenu: false,
    menus: () => [],
    position: () => {
      return { top: 0, left: 0 }
    },
    showLayer: true
  })
  const emit = defineEmits<{
    (e: 'update:show-menu', showMenu: boolean): boolean
    (e: 'triggerItem', item: RightClickMenuItem): RightClickMenuItem
  }>()
  // 监听显隐，控制全局click事件得监听
  watch(
    () => props.showMenu,
    (nv) => {
      if (props.showMenu) {
        document.addEventListener('mousedown', handleOtherClick)
        console.log('right click', props)
      } else {
        document.removeEventListener('mousedown', handleOtherClick)
      }
    }
  )
  const handleOtherClick = (event: any): void => {
    const contextDom = widgetRightClickMenuItemRef.value
    const targetDom = event.target
    if (!(contextDom === targetDom || contextDom?.contains(targetDom))) {
      emit('update:show-menu', false)
    }
  }
  // 位置计算属性
  const widgetRightClickMenuItemRef = ref<HTMLDivElement>()
  const showPosition: Position = reactive({
    top: 0,
    left: 0
  })
  const positionStyle = computed((): { top: string; left: string } => {
    return {
      top: `${showPosition.top < 0 ? 0 : showPosition.top}px`,
      left: `${showPosition.left}px`
    }
  })
  // 监听右键组件位置变化，计算实际显示位置
  watch(
    () => props.position,
    () => {
      nextTick(() => {
        showPosition.top = props.position.top
        showPosition.left = props.position.left
        // 获得屏幕可见区域宽高
        const bodyClientWidth = document.body.clientWidth
        const bodyClientHeight = document.body.clientHeight
        // 获得元素得宽高
        const divClientWidth = widgetRightClickMenuItemRef.value!.clientWidth
        const divClientHeight = widgetRightClickMenuItemRef.value!.clientHeight
        // 高度超过屏幕显示高度时，在上方显示
        if (props.position.top + divClientHeight > bodyClientHeight) {
          showPosition.top = props.position.top - divClientHeight
        }
        // 宽度超过屏幕显示宽度时，在左侧显示
        if (props.position.left + divClientWidth > bodyClientWidth) {
          showPosition.left = props.position.left - divClientWidth
        }
      })
    },
    {
      deep: true
    }
  )
  // 向外发送触发得事件
  const triggerItem = (item: RightClickMenuItem): void => {
    console.log('[ item ]-125', item)
    if (item.children && item.children.length > 0) {
      return
    }
    emit('triggerItem', item)
    // 触发后关闭右键弹窗
    emit('update:show-menu', false)
  }
  // 图层信息
  const layerItem = reactive<{
    list: Widget[]
  }>({ list: [] })
  watch(
    () => props.widget,
    (nv) => {
      layerItem.list = findPath(widgetStore.widgetDataList, nv.key)
    },
    { deep: true }
  )
  // 组件中可包含子组件的组件类型
  const hasListComs = ['HHGrid', 'HHTabPaneWidget']
  function findPath(trees: Widget[], key: string) {
    let path = []
    function traverse(node: Widget) {
      if (!node) {
        return false
      }
      path.push(node)
      if (node.key === key) {
        return true
      }
      if (hasListComs.includes(node.type)) {
        const compList = node.type === 'HHGrid' ? node.children : node.dataProp?.defaultData
        for (const item of compList) {
          for (const child of item.list || []) {
            if (traverse(child)) {
              return true
            }
          }
        }
      }
      path.pop()
      return false
    }
    for (const tree of trees) {
      path = [] // 重置路径
      if (traverse(tree)) {
        return path
      }
    }
    return []
  }
  const changeSelectWidget = (widget: Widget): void => {
    console.log('[ changeSelectWidget ]-203', widget)
    widgetStore.changeSelectWidget(widget)
    // 触发后关闭右键弹窗
    emit('update:show-menu', false)
  }
  // 当前右键组件是否是选中得组件
  const isSelect = computed((): boolean => {
    return widgetStore.selectWidget.key === props.widget.key
  })
</script>

<style lang="scss" scoped>
  .widget-right-click-menu-wrapper {
    position: absolute;
    min-width: 130px;
    min-height: 50px;
    background-color: var(--bg-color-light);
    border-radius: 15px;
    font-size: 12px;
    border: 1px solid var(--border-color-third);
    z-index: 5800;
    color: var(--text-color-primary);
    padding: 20px;
    .item-wrapper {
      width: 100%;
      line-height: 36px;
      cursor: pointer;
      &:hover {
        color: var(--text-color-focus);
      }
      .confirm-icon {
        width: 10px;
        height: 10px;
        margin-right: 10px;
        vertical-align: middle;
      }
      .active-item {
        color: var(--text-color-focus);
      }
    }
    .right-click-menu-item-split-line {
      margin: 10px 0;
      height: 0;
      width: 100%;
      border-bottom: 1px solid var(--border-color-third);
    }
  }
</style>
